import { useState } from 'react';
import { motion } from 'framer-motion';

import { Button } from '@llura/ui';
import { FlowSteps } from '@llura/constants';

import { CoachAvatar } from '../../../shared/coach-avatar';
import { CoachVoicePreview } from '../coach-voice-preview';
import type { CoachGender, CoachTone } from '../../../stores/coach.store';
import { useStepTiming } from '../../../lib/analytics';
import { useStrings } from '../../../hooks/useStrings';

interface RevealStepProps {
  gender: CoachGender;
  tone: CoachTone;
  name: string;
  avatarUrl: string;
  onConfirm: () => void;
}

export function RevealStep({ gender, tone, name, avatarUrl, onConfirm }: RevealStepProps) {
  useStepTiming(FlowSteps.CoachConfiguratorReveal);
  const { strings, interp } = useStrings();
  const toneMeta = strings.coach.tones[tone];
  const firstName = name.split(' ').slice(-1)[0];
  const [voicePlaying, setVoicePlaying] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.55, ease: 'easeOut' }}
      className="flex flex-col items-center gap-7 text-center"
    >
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{strings.onboarding.reveal.yourCoachLabel}</p>

      <motion.div
        initial={{ y: 10 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
      >
        <CoachAvatar name={name} src={avatarUrl} size={160} glow speaking={voicePlaying} />
      </motion.div>

      <CoachVoicePreview gender={gender} tone={tone} onPlayingChange={setVoicePlaying} />

      <div className="space-y-2">
        <h2 className="font-serif text-4xl tracking-tight">{name}</h2>
        <p className="text-sm text-muted-foreground">{toneMeta.tagline}</p>
      </div>

      <p className="max-w-xs text-balance text-sm text-muted-foreground/90">
        {interp(strings.onboarding.reveal.closing, { name: firstName })}
      </p>

      <Button
        onClick={onConfirm}
        size="lg"
        className="mt-2 h-14 rounded-full bg-primary px-10 text-base font-medium uppercase tracking-[0.2em] glow-primary"
      >
        {strings.onboarding.reveal.cta}
      </Button>
    </motion.div>
  );
}
